import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../Styles/CMRFTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { cmrfAPI } from "../../api/index";
import {
  FaUserFriends,
  FaCheckCircle,
  FaRegClock,
  FaRupeeSign,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaTrashAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaHeart,
  FaUpload,
  FaDownload,
  FaPlayCircle,
} from "react-icons/fa";

const AVATAR_COLORS = [
  "cmrf123-avatar-purple",
  "cmrf123-avatar-green",
  "cmrf123-avatar-orange",
];

// Kept only for the filter dropdown / Add-Edit mandal <select> options.
// No longer used to derive display values — the API now returns
// mandal_name / village_name directly, so we trust those.
const MANDAL_NAMES = [
  "Kandukur",
  "Lingasamudram",
  "Gudluru",
  "Ulavapadu",
  "Voletivaripalem",
];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

// ---------- Excel template settings ----------
// Headings follow the Add Beneficiary form fields (same order as the form).
// CONFIRM these with the backend's bulk-upload parser. If it expects different
// heading names, change them here only.
const TEMPLATE_HEADERS = [
  "Beneficiary Name",
  "Relation",
  "Voter Id",
  "Amount (₹)",
  "Mandal",
  "Village",
  "Date",
  "Status",
  "Video URL",
  "Remarks",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 28 }, // Beneficiary Name
  { wch: 24 }, // Relation
  { wch: 16 }, // Voter Id
  { wch: 16 }, // Amount
  { wch: 20 }, // Mandal
  { wch: 22 }, // Village
  { wch: 14 }, // Date
  { wch: 14 }, // Status
  { wch: 40 }, // Video URL
  { wch: 40 }, // Remarks
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Beneficiary Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Relation", "Yes", "2-100 characters (e.g. S/O Ramesh, W/O Suresh, D/O Rajesh)"],
  ["Voter Id", "Yes", "3 capital letters + 7 digits (e.g. ABC1234567)"],
  ["Amount (₹)", "Yes", "Numbers only, greater than 0 (e.g. 50000 or 50000.50)"],
  ["Mandal", "Yes", MANDAL_NAMES.join(", ")],
  ["Village", "Yes", "Alphabets and spaces only. Must exactly match an existing village under the Mandal"],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", STATUS_OPTIONS.join(", ")],
  ["Video URL", "No", "Optional. A link starting with http:// or https:// (e.g. a YouTube link)"],
  ["Remarks", "No", "Optional. If filled: 5-500 characters"],
];

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

// Pulls the most useful human-readable message out of any API error,
// whatever shape the backend returns it in (string, list of {row, reason},
// FastAPI validation array of {loc, msg}, or a network-level failure).
const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}). Check your internet connection or if the backend is down.`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    let videoId = null;

    if (u.hostname.includes("youtu.be")) {
      videoId = u.pathname.slice(1);
    } else if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") {
        videoId = u.searchParams.get("v");
      } else if (u.pathname.startsWith("/embed/")) {
        videoId = u.pathname.split("/embed/")[1];
      } else if (u.pathname.startsWith("/shorts/")) {
        videoId = u.pathname.split("/shorts/")[1];
      }
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  } catch {
    return null;
  }
};

// FIX: use the name fields the backend now returns directly instead of
// re-deriving a label from mandal_id/village_id. The old code showed
// "Village #46" because it ignored row.village_name completely.
const normalizeRow = (row) => ({
  id: row.id,
  name: row.beneficiary_name || "Untitled",
  relation: row.relation_name || "",
  voterId: row.epic_no || "",
  amount: num(row.amount),
  mandalId: row.mandal_id,
  mandal: row.mandal_name || (row.mandal_id ? `Mandal #${row.mandal_id}` : "—"),
  villageId: row.village_id,
  village: row.village_name || (row.village_id ? `Village #${row.village_id}` : "—"),
  date: row.application_date || "",
  status: row.status || "pending",
  video: row.video_url || "",
  remarks: row.remarks || "",
});

const CmrfTable = () => {
  const navigate = useNavigate();

  const [cmrfData, setCmrfData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10;

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [videoItem, setVideoItem] = useState(null);

  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchCmrfData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (mandalFilter !== "All Mandals") params.mandal_name = mandalFilter;
      if (statusFilter !== "All Status") params.status = statusFilter;

      const res = await cmrfAPI.getAll(params);
      const rows = extractList(res).map(normalizeRow);
      setCmrfData(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, mandalFilter, statusFilter]);

  useEffect(() => {
    fetchCmrfData();
  }, [fetchCmrfData]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + cmrfData.length;

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");

    pages.push(totalPages);
    return pages;
  };

  const approvedCount = cmrfData.filter((i) => i.status === "approved").length;
  const pendingCount = cmrfData.filter((i) => i.status === "pending").length;
  const totalAmount = cmrfData.reduce((sum, i) => sum + i.amount, 0);

  // FIX: pre-fill villageName/mandalName with the actual values already
  // returned by the API, instead of forcing the user to retype the
  // village name blind on every edit.
  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      ...item,
      mandalName: item.mandal || "",
      villageName: item.village && item.village !== "—" ? item.village : "",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();

    const trimmedVillage = editForm.villageName.trim();

    if (!trimmedVillage) {
      toast.error("Please enter the village name to save");
      return;
    }

    const payload = {
      beneficiary_name: editForm.name,
      relation_name: editForm.relation,
      epic_no: editForm.voterId || undefined,
      amount: editForm.amount ? Number(editForm.amount) : undefined,
      mandal_name: editForm.mandalName,
      village_name: trimmedVillage,
      application_date: editForm.date || undefined,
      status: editForm.status,
      video_url: editForm.video || undefined,
      remarks: editForm.remarks || undefined,
    };

    setSaving(true);
    try {
      await cmrfAPI.update(editForm.id, payload);
      toast.success("Beneficiary updated");
      closeEditModal();
      fetchCmrfData();
    } catch (err) {
      console.error("CMRF update payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (item) => {
    setDeleteItem(item);
  };

  const closeDeleteModal = () => {
    setDeleteItem(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await cmrfAPI.delete(deleteItem.id);
      toast.success("Deleted successfully");
      closeDeleteModal();
      fetchCmrfData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const openVideoModal = (item) => setVideoItem(item);
  const closeVideoModal = () => setVideoItem(null);

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 22 }, { wch: 10 }, { wch: 85 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "CMRF_Beneficiaries_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const handleBulkUploadClick = () => bulkFileInputRef.current?.click();

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setBulkUploading(true);
    try {
      const res = await cmrfAPI.bulkUpload(file);
      toast.success(`Import complete — ${num(res.data?.inserted)} beneficiaries added`);
      fetchCmrfData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="cmrf123-page">
      <input
        ref={bulkFileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleBulkFileSelected}
      />

      <div className="cmrf123-header">
        <div className="cmrf123-header-left">
          <div className="cmrf123-header-icon">
            <FaUserFriends />
          </div>
          <div>
            <h2 className="cmrf123-title">CM Relief Fund Beneficiaries</h2>
            <p className="cmrf123-subtitle">
              View and manage beneficiaries of Chief Minister Relief Fund
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="cmrf123-table-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="cmrf123-add-plus">
              <FaDownload />
            </span>{" "}
            Download Template
          </button>

          <button
            className="cmrf123-table-add-btn"
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
            style={{ background: "#0b3d24" }}
          >
            <span className="cmrf123-add-plus">
              {bulkUploading ? "⏳" : <FaUpload />}
            </span>{" "}
            {bulkUploading ? "Uploading..." : "Bulk Upload (Excel)"}
          </button>

          <button
            className="cmrf123-table-add-btn"
            onClick={() => navigate("/dashboard/cmrf-form")}
          >
            <span className="cmrf123-add-plus">+</span> Add New Beneficiary
          </button>
        </div>
      </div>

      <div className="cmrf123-scroll-body">
        <div className="cmrf123-stats-row">
          <div className="cmrf123-stat-card">
            <div className="cmrf123-stat-icon cmrf123-icon-green">
              <FaUserFriends />
            </div>
            <div>
              <p className="cmrf123-stat-label">Total Beneficiaries</p>
              <h3 className="cmrf123-stat-value">{totalCount}</h3>
              <p className="cmrf123-stat-sub">All time records</p>
            </div>
          </div>

          <div className="cmrf123-stat-card">
            <div className="cmrf123-stat-icon cmrf123-icon-blue">
              <FaCheckCircle />
            </div>
            <div>
              <p className="cmrf123-stat-label">Approved Cases</p>
              <h3 className="cmrf123-stat-value">{approvedCount}</h3>
              <p className="cmrf123-stat-sub cmrf123-sub-blue">
                {cmrfData.length ? ((approvedCount / cmrfData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="cmrf123-stat-card">
            <div className="cmrf123-stat-icon cmrf123-icon-orange">
              <FaRegClock />
            </div>
            <div>
              <p className="cmrf123-stat-label">Pending Cases</p>
              <h3 className="cmrf123-stat-value">{pendingCount}</h3>
              <p className="cmrf123-stat-sub cmrf123-sub-orange">
                {cmrfData.length ? ((pendingCount / cmrfData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="cmrf123-stat-card">
            <div className="cmrf123-stat-icon cmrf123-icon-purple">
              <FaRupeeSign />
            </div>
            <div>
              <p className="cmrf123-stat-label">Total Amount Disbursed</p>
              <h3 className="cmrf123-stat-value">
                ₹{totalAmount.toLocaleString("en-IN")}
              </h3>
              <p className="cmrf123-stat-sub cmrf123-sub-purple">
                This page
              </p>
            </div>
          </div>
        </div>

        <div className="cmrf123-table-filter-section">
          <div className="cmrf123-search-wrapper">
            <FaSearch className="cmrf123-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary by name, relation..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="cmrf123-select-wrapper">
            <FaMapMarkerAlt className="cmrf123-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              {MANDAL_NAMES.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="cmrf123-select-wrapper">
            <FaFilter className="cmrf123-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All Status">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>

          <button className="cmrf123-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        <div className="cmrf123-table-wrapper">
          <table className="cmrf123-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Relation</th>
                <th>Amount</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Video</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="cmrf123-no-data">
                    Loading…
                  </td>
                </tr>
              ) : cmrfData.length > 0 ? (
                cmrfData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="cmrf123-name-cell">
                        <span
                          className={`cmrf123-avatar ${
                            AVATAR_COLORS[item.id % AVATAR_COLORS.length]
                          }`}
                        >
                          <FaUserCircle />
                        </span>
                        <span className="cmrf123-name-text">
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="cmrf123-relation-cell">
                      {item.relation}
                    </td>
                    <td className="cmrf123-amount">
                      ₹{item.amount.toLocaleString("en-IN")}
                    </td>
                    <td>
                      <span className="cmrf123-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="cmrf123-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`cmrf123-status ${
                          item.status === "approved"
                            ? "cmrf123-approved"
                            : "cmrf123-pending"
                        }`}
                      >
                        {item.status === "approved" ? (
                          <FaCheckCircle />
                        ) : (
                          <FaRegClock />
                        )}{" "}
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td>
                      {item.video ? (
                        <button
                          type="button"
                          onClick={() => openVideoModal(item)}
                          title="Play video"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#16a34a",
                            cursor: "pointer",
                            fontSize: "20px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FaPlayCircle />
                        </button>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>—</span>
                      )}
                    </td>
                    <td className="cmrf123-action-cell">
                      <button
                        className="cmrf123-edit-btn"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="cmrf123-delete-btn"
                        onClick={() => openDeleteModal(item)}
                      >
                        <FaTrashAlt /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="cmrf123-no-data">
                    No matching beneficiaries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="cmrf123-footer">
          <p className="cmrf123-footer-text">
            {totalCount > 0
              ? `Showing ${startIndex + 1} to ${endIndex} of ${totalCount} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="cmrf123-pagination">
            <button
              className="cmrf123-page-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft />
            </button>

            {getPageNumbers().map((page, i) =>
              page === "..." ? (
                <span key={`dots-${i}`} className="cmrf123-page-dots">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`cmrf123-page-btn ${
                    currentPage === page ? "cmrf123-page-active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="cmrf123-page-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="cmrf123-scheme-banner">
          <div className="cmrf123-banner-icon-left">
            <FaHeart />
          </div>

          <div className="cmrf123-banner-text">
            <h4>CM Relief Fund</h4>
            <p>
              Financial assistance is provided to the needy and deserving
              people for medical emergencies and other critical situations.
            </p>
          </div>

          <div className="cmrf123-banner-icon-right">
            <FaHeart />
          </div>
        </div>
      </div>

      {editItem && editForm && (
        <div className="cmrf123-modal-overlay" onClick={closeEditModal}>
          <div
            className="cmrf123-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cmrf123-modal-header">
              <h3>Edit Beneficiary</h3>
              <button
                className="cmrf123-modal-close"
                onClick={closeEditModal}
              >
                <FaTimes />
              </button>
            </div>

            <form className="cmrf123-modal-form" onSubmit={handleEditSave}>
              <div className="cmrf123-form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  required
                />
              </div>

              <div className="cmrf123-form-row">
                <label>Relation</label>
                <input
                  type="text"
                  value={editForm.relation}
                  onChange={(e) =>
                    handleEditChange("relation", e.target.value)
                  }
                  required
                />
              </div>

              <div className="cmrf123-form-grid">
                <div className="cmrf123-form-row">
                  <label>Amount</label>
                  <input
                    type="text"
                    value={editForm.amount}
                    onChange={(e) =>
                      handleEditChange("amount", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="cmrf123-form-row">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      handleEditChange("status", e.target.value)
                    }
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cmrf123-form-grid">
                <div className="cmrf123-form-row">
                  <label>Village *</label>
                  <input
                    type="text"
                    placeholder="Enter Village Name"
                    value={editForm.villageName}
                    onChange={(e) =>
                      handleEditChange("villageName", e.target.value)
                    }
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    Must exactly match an existing village name under the selected Mandal.
                  </small>
                </div>

                <div className="cmrf123-form-row">
                  <label>Mandal</label>
                  <select
                    value={editForm.mandalName}
                    onChange={(e) =>
                      handleEditChange("mandalName", e.target.value)
                    }
                  >
                    {MANDAL_NAMES.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cmrf123-form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) =>
                    handleEditChange("date", e.target.value)
                  }
                  required
                />
              </div>

              <div className="cmrf123-form-row">
                <label>Video URL</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={editForm.video}
                  onChange={(e) =>
                    handleEditChange("video", e.target.value)
                  }
                />
              </div>

              <div className="cmrf123-modal-actions">
                <button
                  type="button"
                  className="cmrf123-modal-cancel-btn"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="cmrf123-modal-save-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteItem && (
        <div className="cmrf123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="cmrf123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cmrf123-confirm-icon">
              <FaTrashAlt />
            </div>
            <h3 className="cmrf123-confirm-title">
              Delete this beneficiary?
            </h3>
            <p className="cmrf123-confirm-text">
              Are you sure you want to delete{" "}
              <strong>{deleteItem.name}</strong> from the beneficiary list?
              This action cannot be undone.
            </p>

            <div className="cmrf123-confirm-actions">
              <button
                className="cmrf123-confirm-cancel-btn"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                className="cmrf123-confirm-delete-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {videoItem && (
        <div className="cmrf123-modal-overlay" onClick={closeVideoModal}>
          <div
            className="cmrf123-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "720px" }}
          >
            <div className="cmrf123-modal-header">
              <h3>{videoItem.name} — Video</h3>
              <button
                className="cmrf123-modal-close"
                onClick={closeVideoModal}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              {getYouTubeEmbedUrl(videoItem.video) ? (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%",
                  }}
                >
                  <iframe
                    src={getYouTubeEmbedUrl(videoItem.video)}
                    title={`${videoItem.name} video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              ) : (
                <video
                  src={videoItem.video}
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    borderRadius: "8px",
                    background: "#000",
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CmrfTable;